<template>
    <DividerTitle :title="title" />
    <v-row class="d-flex justify-start">
        <!-- Country Selection -->
        <v-col
            cols="12"
            :sm="useAsFilter ? 6 : 3"
            :md="useAsFilter ? 6 : 4"
            :lg="useAsFilter ? 6 : 2"
        >
            <v-autocomplete
                ref="country"
                :data-cy="`${dataCyPrefix}-country`"
                v-model="localLocation.country_code"
                :items="countries"
                item-title="name"
                item-value="code"
                :label="$t('formFields.country')"
                @update:modelValue="handleCountrySelection"
                v-auto-animate
                :rules="[requiredRule]"
                :density="density"
                hide-details="auto"
            ></v-autocomplete>
        </v-col>

        <!-- Place Selection (Google Places Autocomplete) -->
        <v-col
            cols="12"
            :sm="useAsFilter ? 6 : 3"
            :md="useAsFilter ? 6 : 5"
            :lg="useAsFilter ? 6 : 3"
        >
            <v-autocomplete
                ref="place"
                :data-cy="`${dataCyPrefix}-city`"
                v-model="placeDisplay"
                v-model:search="placeSearch"
                :items="suggestions"
                item-title="description"
                item-value="place_id"
                :label="$t('formFields.city')"
                :loading="isFetchingPlaces"
                :no-filter="true"
                :disabled="!localLocation.country_code"
                :no-data-text="
                    (placeSearch || '').length < 3
                        ? $t('addPaymentIncident.typeThreeChars')
                        : $t('addPaymentIncident.noResults')
                "
                variant="outlined"
                v-auto-animate
                :rules="[requiredRule]"
                :density="density"
                hide-details="auto"
                @update:search="onPlaceSearch"
                @update:model-value="onPlaceSelect"
            />
        </v-col>

        <!-- Postal Code -->
        <v-col v-if="!useAsFilter" sm="3" md="3" xs="12" lg="2">
            <v-text-field
                ref="postalCode"
                :data-cy="`${dataCyPrefix}-postal-code`"
                v-model="localLocation.postalCode"
                :label="$t('formFields.postalCode')"
                v-auto-animate
                :rules="[postalRequiredRule]"
                hide-details="auto"
            />
        </v-col>
        
        <!-- Date -->
        <v-col v-if="!useAsFilter" sm="3" md="3" xs="12" lg="2">
            <FormDate
                ref="formDate"
                v-model="localLocation"
                :data-cy-prefix="dataCyPrefix"
            />
        </v-col>

        <!-- Time -->
        <v-col v-if="!useAsFilter" sm="3" md="3" xs="12" lg="2">
            <FormTime
                ref="formTime"
                v-model="localLocation"
                :data-cy-prefix="dataCyPrefix"
                :required="!useAsFilter"
            />
        </v-col>
    </v-row>
</template>

<script>
import FormDate from "./FormDate.vue";
import FormTime from "./FormTime.vue";
import { placesAutocomplete } from "@/api";
import DividerTitle from "./DividerTitle.vue";
import { mapActions, mapState } from "vuex";
import emitter from "@/eventBus";
import { showAlertModal } from "@/utils";

const DEBOUNCE_MS = 350;

export default {
    components: { FormDate, FormTime, DividerTitle },
    props: {
        modelValue: Object,
        formType: String,
        title: String,
        useAsFilter: Boolean,
        density: {
            type: String,
            default: undefined,
        },
        dataCyPrefix: String,
    },
    emits: ["update:modelValue"],
    data() {
        return {
            isFetchingPlaces: false,
            placeSearch: "",
            placeDisplay: null,
            suggestions: [],
            searchDebounce: null,
            localLocation: {
                country_code: "",
                place_id: "",
                place_display_name: "",
                postalCode: "",
                date: [],
                dateStart: null,
                dateEnd: null,
                timeStart: null,
                timeEnd: null,
                ...this.modelValue,
            },
            requiredRule: (value) => !!value || this.$t("validation.required"),
            postalRequiredRule: (value) =>
                !!String(value ?? "").trim() || this.$t("validation.required"),
        };
    },
    computed: {
        ...mapState(["countries", "selectedLanguage"]),
    },
    async mounted() {
        await this.fetchCountries();
        emitter.on("language-changed", async (newLang) => {
            this.$store.state.selectedLanguage = newLang;
            await this.fetchCountries();
        });
    },
    unmounted() {
        emitter.off("language-changed");
    },
    methods: {
        ...mapActions(["fetchCountries"]),
        showAlertModal,
        onPlaceSearch(value) {
            const term = (value || "").trim();
            if (this.suggestions?.length && this.suggestions.some((s) => s.description === term)) {
                return;
            }
            if (this.searchDebounce) clearTimeout(this.searchDebounce);
            if (term.length < 3 || !this.localLocation.country_code) {
                this.suggestions = [];
                return;
            }
            this.isFetchingPlaces = true;
            this.searchDebounce = setTimeout(() => {
                this.searchDebounce = null;
                this.fetchSuggestions(term);
            }, DEBOUNCE_MS);
        },
        async fetchSuggestions(term) {
            if (!this.localLocation.country_code) {
                this.suggestions = [];
                this.isFetchingPlaces = false;
                return;
            }
            try {
                const lang = this.$i18n?.locale || "en";
                const res = await placesAutocomplete(term, lang, this.localLocation.country_code);
                this.suggestions = res.predictions || [];
            } catch (e) {
                this.suggestions = [];
                this.showAlertModal(this.$store, "Error fetching places", "danger", 8000, e);
            } finally {
                this.isFetchingPlaces = false;
            }
        },
        onPlaceSelect(selected) {
            if (selected === null || selected === undefined || selected === "") {
                this.localLocation.place_id = "";
                this.localLocation.place_display_name = "";
                this.placeDisplay = null;
                this.emitUpdate();
                return;
            }
            const placeId = typeof selected === "string" ? selected : selected?.place_id;
            const item = this.suggestions.find((s) => s.place_id === placeId);
            const description = item?.description || placeId;
            this.localLocation.place_id = placeId;
            this.localLocation.place_display_name = description;
            this.placeDisplay = { place_id: placeId, description };
            this.emitUpdate();
        },
        handleCountrySelection(code) {
            this.localLocation.country_code = code;
            this.localLocation.place_id = "";
            this.localLocation.place_display_name = "";
            this.placeDisplay = null;
            this.suggestions = [];
            this.emitUpdate();
        },
        async validateFields() {
            await this.$nextTick();
            let isValid = true;

            const touchInput = async (componentRef) => {
                if (!componentRef?.validate) return;
                await componentRef.validate();
                if (componentRef.errorMessages?.length > 0) {
                    isValid = false;
                }
            };

            await touchInput(this.$refs.country);
            await touchInput(this.$refs.place);

            if (!this.useAsFilter) {
                await touchInput(this.$refs.postalCode);
                if (this.$refs.formDate?.validateFields) {
                    const dateOk = await this.$refs.formDate.validateFields();
                    if (!dateOk) isValid = false;
                }
                if (this.$refs.formTime?.validateFields) {
                    const timeOk = await this.$refs.formTime.validateFields();
                    if (!timeOk) isValid = false;
                }
            }

            return isValid;
        },
        resetValidation() {
            this.$refs.country?.resetValidation?.();
            this.$refs.place?.resetValidation?.();
            this.$refs.postalCode?.resetValidation?.();
            this.$refs.formDate?.resetValidation?.();
            this.$refs.formTime?.resetValidation?.();
        },
        emitUpdate() {
            this.$emit("update:modelValue", {
                ...this.localLocation,
                country_code: this.localLocation.country_code,
                place_id: this.localLocation.place_id,
                place_display_name: this.localLocation.place_display_name,
                date: this.localLocation.date,
                dateStart: this.localLocation.dateStart,
                dateEnd: this.localLocation.dateEnd,
            });
        },
    },
    watch: {
        localLocation: {
            handler() {
                this.emitUpdate();
            },
            deep: true,
        },
        modelValue: {
            handler(newVal) {
                if (newVal && JSON.stringify(newVal) !== JSON.stringify(this.localLocation)) {
                    this.localLocation = {
                        country_code: newVal.country_code || "",
                        place_id: newVal.place_id || "",
                        place_display_name: newVal.place_display_name || "",
                        postalCode: newVal.postalCode || "",
                        date: [...(newVal.date || [])],
                        dateStart: newVal.dateStart,
                        dateEnd: newVal.dateEnd,
                        timeStart: newVal.timeStart,
                        timeEnd: newVal.timeEnd,
                    };
                    if (newVal.place_id && newVal.place_display_name) {
                        this.placeDisplay = {
                            place_id: newVal.place_id,
                            description: newVal.place_display_name,
                        };
                    } else {
                        this.placeDisplay = null;
                    }
                }
            },
            deep: true,
        },
    },
};
</script>
